import styled, { css } from 'styled-components';
import { gridTokens } from '../../tokens/components/Grid/tokens';

import {
  MOBILE_WIDTH,
  TABLET_PORTRAIT_WIDTH,
  TABLET_LANDSCAPE_WIDTH,
  DESKTOP_WIDTH,
} from '../../helpers/constants/responsive-breakpoints';

type BasicSizes =
  | '1-of-12'
  | '2-of-12'
  | '3-of-12'
  | '4-of-12'
  | '5-of-12'
  | '6-of-12'
  | '7-of-12'
  | '8-of-12'
  | '9-of-12'
  | '10-of-12'
  | '11-of-12'
  | '12-of-12';

export type ColumnSizes = BasicSizes | 'hidden';
export type OffsetSizes = BasicSizes | '0-of-12';

const defaultColumnWidth = 100 / 12;

const columnSize = size => {
  if (!size) return css``;
  if (size === 'hidden')
    return css`
      display: none;
    `;

  const index = size.replace(/-of-12/i, '');
  const value = defaultColumnWidth * index;
  return css`
    display: block;
    flex: 0 0 ${value}%;
    max-width: ${value}%;
  `;
};

const columnOffset = offset => {
  if (!offset) return css``;

  const index = offset.replace(/-of-12/i, '');
  const value = defaultColumnWidth * index;
  return css`
    margin-left: ${value}%;
  `;
};

const defaultSize = () => {
  return css`
    flex: 0 0 ${defaultColumnWidth}%;
    max-width: ${defaultColumnWidth}%;
    padding: calc(${gridTokens.CoreGutterDefault} / 2);
  `;
};

const mediaQueries = theme => {
  const sideNav = theme.hasOwnProperty('sideNavWidth');
  const mobileBreakpoint = sideNav ? MOBILE_WIDTH + theme.sideNavWidth : MOBILE_WIDTH;
  const tabletPortraitBreakpoint = sideNav ? TABLET_PORTRAIT_WIDTH + theme.sideNavWidth : TABLET_PORTRAIT_WIDTH;
  const tabletLandscapeBreakpoint = sideNav ? TABLET_LANDSCAPE_WIDTH + theme.sideNavWidth : TABLET_LANDSCAPE_WIDTH;
  const desktopBreakpoint = sideNav ? DESKTOP_WIDTH + theme.sideNavWidth : DESKTOP_WIDTH;

  const mobileMaxWidth = `${mobileBreakpoint}px`;
  const tabletPortraitMinWidth = `${mobileBreakpoint + 1}px`;
  const tabletPortraitMaxWidth = `${tabletPortraitBreakpoint}px`;
  const tabletLandscapeMinWidth = `${tabletPortraitBreakpoint + 1}px`;
  const tabletLandscapeMaxWidth = `${tabletLandscapeBreakpoint}px`;
  const desktopMinWidth = `${tabletLandscapeBreakpoint + 1}px`;
  const desktopMaxWidth = `${desktopBreakpoint}px`;
  const desktopLargeMinWidth = `${desktopBreakpoint + 1}px`;

  return css`
    @media screen and (min-width: 0) {
      ${columnSize(theme.columnMobileSize)}
      ${columnOffset(theme.columnOffsetMobile)}
    }

    @media screen and (min-width: 0) and (max-width: ${mobileMaxWidth}) {
      ${columnOffset(theme.columnOffsetMobileOnly)}
    }

    @media screen and (min-width: ${tabletPortraitMinWidth}) {
      ${columnSize(theme.columnTabletPortraitSize)}
      ${columnOffset(theme.columnOffsetTabletPortrait)}
    }

    @media screen and (min-width: ${tabletPortraitMinWidth}) and (max-width: ${tabletPortraitMaxWidth}) {
      ${columnOffset(theme.columnOffsetTabletPortraitOnly)}
    }

    @media screen and (min-width: ${tabletLandscapeMinWidth}) {
      ${columnSize(theme.columnTabletLandscapeSize)}
      ${columnOffset(theme.columnOffsetTabletLandscape)}
    }

    @media screen and (min-width: ${tabletLandscapeMinWidth}) and (max-width: ${tabletLandscapeMaxWidth}) {
      ${columnOffset(theme.columnOffsetTabletLandscapeOnly)}
    }

    @media screen and (min-width: ${desktopMinWidth}) {
      ${columnSize(theme.columnDesktopSize)}
      ${columnOffset(theme.columnOffsetDesktop)}
    }

    @media screen and (min-width: ${desktopMinWidth}) and (max-width: ${desktopMaxWidth}) {
      ${columnOffset(theme.columnOffsetDesktopOnly)}
    }

    @media screen and (min-width: ${desktopLargeMinWidth}) {
      ${columnSize(theme.columnDesktopLargeSize)}
      ${columnOffset(theme.columnOffsetDesktopLarge)}
    }
  `;
};

export const ColumnWrapper = styled.div`
  ${defaultSize}
  ${({ theme }) => mediaQueries(theme)}

  ${({ theme }) =>
    theme.gridSmall &&
    css`
      padding: calc(${gridTokens.CoreGutterSmall} / 2);
    `}
`;

export const RowWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  margin: calc(${gridTokens.CoreGutterDefault} / -2);

  & + & {
    padding-top: calc(${gridTokens.CoreGutterDefault} / 2);
  }

  ${({ theme }) =>
    theme.gridSmall &&
    css`
      margin: calc(${gridTokens.CoreGutterSmall} / -2);

      & + & {
        padding-top: calc(${gridTokens.CoreGutterSmall} / 2);
      }
    `}
`;

export const GridWrapper = styled.div`
  margin-inline: auto;
  padding: 0 ${gridTokens.CoreOffsetDefault};

  ${({ theme }) =>
    theme.gridSmall &&
    css`
      padding: 0 ${gridTokens.CoreOffsetSmall};

      ${theme.gridCompact &&
      css`
        padding: 0;
        > ${RowWrapper} {
          margin: 0;
          > ${ColumnWrapper} {
            padding: 0;
          }
        }
      `}
    `}

  ${({ theme }) =>
    !theme.isFullWidth &&
    css`
      max-width: ${gridTokens.CoreWidthMax};
    `}
`;
